import { PlatformFeatureType } from '../types/platform-features.type';
import { StudioType } from '../types/studio.type';
import { type VoiceConfig, VoiceMode } from '../types/webclient-config.type';

type VoiceStudioContext = {
  studioType: StudioType;
  platformFeatures?: PlatformFeatureType[];
};

type VoiceModeDerivationInput = VoiceStudioContext & {
  voice?: VoiceConfig;
};

const hasWebclientVoiceFeature = (
  platformFeatures?: PlatformFeatureType[],
): boolean =>
  Boolean(platformFeatures?.includes(PlatformFeatureType.WebclientVoice));

/**
 * Whether the studio can offer voice at all. A Voice A.I. Studio is voice-only
 * by definition, so it does not depend on the WebclientVoice feature.
 */
export const isVoiceConfigurable = ({
  studioType,
  platformFeatures,
}: VoiceStudioContext): boolean => {
  if (studioType === StudioType.VOICE_AI) {
    return true;
  }

  return hasWebclientVoiceFeature(platformFeatures);
};

/**
 * Derives the voice mode of the widget. The mode is never stored: the studio
 * type decides it first, then the WebclientVoice feature, and only a chat
 * studio with the feature falls through to the stored `voice.enabled` toggle.
 */
export const deriveVoiceMode = ({
  studioType,
  platformFeatures,
  voice,
}: VoiceModeDerivationInput): VoiceMode => {
  if (studioType === StudioType.VOICE_AI) {
    return VoiceMode.VoiceOnly;
  }

  if (!hasWebclientVoiceFeature(platformFeatures)) {
    return VoiceMode.Chat;
  }

  if (voice?.enabled) {
    return VoiceMode.ChatAndVoice;
  }

  return VoiceMode.Chat;
};
